import styled from "styled-components";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { borderRadius } from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import {
  Modal,
  ModalHeader,
} from "../../../design-system/components/composite/Modal/Modal";

/**
 * Styles for the story "Schedule refresh" modal.
 *
 * Two rules apply throughout, and both come from review:
 *  - No `px` except 1px hairline borders — everything else is a token or `rem`.
 *  - No `height`. Figma stamps a measured height on every auto-layout frame
 *    (the modal at 290/636/607, the field groups at 65/61, the labels at 17)
 *    but those are outputs of the content. The modal grows with whatever the
 *    selected preset renders; `Modal size="sm"` caps it at 90vh and the body
 *    scrolls.
 *
 * Figma's 400px shell is supplied by the existing `Modal size="sm"` variant.
 */

/** Figma uses the design-system small modal with a `stroke/100` frame. */
export const StoryModal = styled(Modal)`
  border: 1px solid ${rawColors.stroke[100]};
`;

/**
 * Header row. `ModalHeader` defaults to the generic `elevation[2]` shadow;
 * this form uses the Figma modal-header shadow instead. Scoped here rather
 * than changed in `ModalHeader` itself, since that would restyle the header
 * of every modal in the app.
 */
export const Header = styled(ModalHeader)`
  background-color: ${({ theme }) => theme.colors.background.primary};
  box-shadow: ${({ theme }) => theme.shadows.component.modalHeader};
`;

/** Modal title — Figma Body 2 (SemiBold 14/24). */
export const HeaderTitle = styled.h2`
  margin: 0;
  ${typographyMixin("body", 2)}
  color: ${({ theme }) => theme.colors.text.primary};
`;

/**
 * The body column. `width: 100%` rather than Figma's 560px — 560 is just
 * 600 minus the 20px padding either side, so 100% is the same number and
 * survives the modal narrowing to 90% on a small viewport.
 */
export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  width: 100%;
  min-width: 0;
`;

/**
 * Footer button row. Both buttons are `flex: 1 1 0` so they stay a true 50/50
 * regardless of label length — "Save schedule" must not steal width from
 * "Cancel".
 */
export const FooterRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${spacing[2]};
  width: 100%;
`;

/**
 * Footer button slot.
 *
 * The radius override is deliberate: Figma specifies 8px for both footer
 * buttons, but `Button size="md"` resolves `semantic.button.md`, which is 6px.
 * Retuning that token would change every medium button in the app, so it is
 * scoped here — and flagged, because the 6px/8px mismatch between the button
 * scale and the Figma modal spec is a design-system question, not this
 * modal's to settle.
 */
export const FooterAction = styled.div`
  flex: 1 1 0;
  min-width: 0;

  > button {
    border-radius: ${borderRadius.lg};
  }
`;
